import { useState, useEffect } from 'react'
import { ref, onValue, update, remove, get } from 'firebase/database'
import { useNavigate } from 'react-router-dom'
import { database } from '@lib/firebase'
import useAuth from '@hooks/useAuth'
import { es } from 'date-fns/locale'
import { format } from 'date-fns'
import UseWhatsapp from 'whatsapp-react-component'

export const useAdminPanel = () => {
  const [activeSection, setActiveSection] = useState('dashboard')
  const [notifications, setNotifications] = useState([])
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [appointments, setAppointments] = useState([])
  const [lastChecked, setLastChecked] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [darkMode, setDarkMode] = useState(false)
  const [reminders, setReminders] = useState([])
  const [messages, setMessages] = useState([])
  const { user, loading, logout } = useAuth()
  const navigate = useNavigate()
  const [dashboardData, setDashboardData] = useState({
    totalAppointments: 0,
    upcomingAppointments: 0,
    totalMessages: 0,
    appointmentsByType: { optica: 0, domicilio: 0 },
  })

  useEffect(() => {
    if (!user) return

    const fetchData = (ref, updateFunction) => {
      return onValue(ref, (snapshot) => {
        const data = snapshot.val()
        if (data) {
          updateFunction(data)
        }
      })
    }

    const appointmentsRef = ref(database, 'citas')
    const messagesRef = ref(database, 'contactenos')

    const unsubscribeAppointments = fetchData(appointmentsRef, (data) => {
      const appointmentList = Object.entries(data).flatMap(([userId, userAppointments]) =>
        Object.entries(userAppointments).map(([appointmentId, appointment]) => ({
          id: appointmentId,
          userId,
          ...appointment,
          date: new Date(appointment.date),
        })),
      )

      setDashboardData(prev => ({
        ...prev,
        totalAppointments: appointmentList.length,
        upcomingAppointments: appointmentList.filter((app) => app.date > new Date()).length,
        appointmentsByType: appointmentList.reduce(
          (acc, app) => {
            acc[app.type] = (acc[app.type] || 0) + 1
            return acc
          },
          { optica: 0, domicilio: 0 },
        ),
      }))
    })

    const unsubscribeMessages = fetchData(messagesRef, (data) => {
      const messageList = Object.entries(data).flatMap(([userId, userMessages]) =>
        Object.entries(userMessages).map(([messageId, message]) => ({
          id: messageId,
          userId,
          ...message,
          timestamp: new Date(message.timestamp),
        })),
      )

      setDashboardData((prev) => ({
        ...prev,
        totalMessages: messageList.length,
      }))
    })

    return () => {
      unsubscribeAppointments()
      unsubscribeMessages()
    }
  }, [user])

  useEffect(() => {
    if (!loading && !user) navigate('/login')
  }, [user, loading, navigate])

  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode')
    savedMode === 'true' && setDarkMode(true)
  }, [])

  useEffect(() => {
    if (!user) return

    const appointmentsRef = ref(database, 'citas')
    const appointmentsUnsubscribe = onValue(appointmentsRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const appointmentList = Object.entries(data).flatMap(([userId, userAppointments]) =>
          Object.entries(userAppointments).map(([appointmentId, appointment]) => ({
            id: appointmentId,
            userId,
            ...appointment,
            date: new Date(appointment.date),
          })),
        )

        setAppointments((prevAppointments) => {
          if (JSON.stringify(prevAppointments) !== JSON.stringify(appointmentList)) {
            return appointmentList
          }
          return prevAppointments
        })

        const now = Date.now()
        const lastCheckedData = lastChecked || {}
        Object.entries(data).forEach(([userId, userAppointments]) => {
          Object.entries(userAppointments).forEach(([appointmentId, appointment]) => {
            const appointmentKey = `${userId}-${appointmentId}`
            const notificationId = `citas/${appointmentKey}/${Date.now()}`
            if (!lastCheckedData[appointmentKey]) {
              const notification = {
                id: notificationId,
                type: 'citas',
                message: `Nueva cita ${appointment.type === 'domicilio' ? 'a domicilio' : 'en óptica'} de ${userId}`,
                timestamp: now,
              }

              setNotifications((prev) => [...prev, notification])
              setReminders((prev) => [...prev, notification])
              setLastChecked((prev) => ({ ...prev, [appointmentKey]: now }))

              update(ref(database, `citas/${userId}/${appointmentId}`), {
                notified: true,
              })

              setTimeout(() => {
                setNotifications((prev) => prev.filter((n) => n.id !== notification.id))
              }, 10000)
            }
          })
        })
      }
    })

    const messagesRef = ref(database, 'contactenos')
    const messagesUnsubscribe = onValue(messagesRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const messageList = Object.entries(data).flatMap(([userId, userMessages]) =>
          Object.entries(userMessages).map(([messageId, message]) => ({
            id: messageId,
            userId,
            ...message,
            timestamp: new Date(message.timestamp),
          })),
        )

        setMessages(prevMessageList => {
          if (JSON.stringify(prevMessageList) !== JSON.stringify(messageList)) {
            return messageList
          }
          return prevMessageList
        })

        const now = Date.now()
        const lastCheckedData = lastChecked || {}

        Object.entries(data).forEach(([userId, userMessages]) => {
          Object.entries(userMessages).forEach(([messageId, message]) => {
            const messageKey = `${userId}-${messageId}`
            const notificationId = `contactenos-${messageKey}-${Date.now()}`
            if (!lastCheckedData[messageKey]) {
              const notification = {
                id: notificationId,
                type: 'contactenos',
                message: `Nuevo mensaje de ${message.name}`,
                timestamp: Date.now(),
              }
              setNotifications((prev) => [...prev, notification])
              setReminders((prev) => [...prev, notification])
              setLastChecked((prev) => ({ ...prev, [messageKey]: now }))

              update(ref(database, `contactenos/${userId}/${messageId}`), { notified: true })

              setTimeout(() => {
                setNotifications((prev) => prev.filter((n) => n.id !== notification.id))
              }, 10000)
            }
          })
        })
      } else {
        setMessages([])
      }
    })

    const reminderInterval = setInterval(() => {
        const now = new Date()
        appointments.forEach((appointment) => {
          if (appointment.status === 'Confirmado') {
            const appointmentTime = new Date(appointment.date)
            const timeDiff = appointmentTime.getTime() - now.getTime()
            const minutesDiff = Math.floor(timeDiff / (1000 * 60))

            if (minutesDiff <= 30 && minutesDiff > 0) {
              setNotifications((prev) => [
                ...prev,
                {
                  id: `reminder-${appointment.id}`,
                  type: 'reminder',
                  message: `Recordatorio: Cita con ${appointment.name} en ${minutesDiff} minutos`,
                  timestamp: Date.now(),
                },
              ])
            }
          }
        })
      },
      10 * 60 * 1000,
    )

    return () => {
      appointmentsUnsubscribe()
      messagesUnsubscribe()
      clearInterval(reminderInterval)
    }
  }, [lastChecked, user, appointments])

  const toggleDarkMode = () => {
    setDarkMode((prevMode) => {
      const newMode = !prevMode
      localStorage.setItem('darkMode', newMode)
      return newMode
    })
  }

  const handleAppointmentStatus = async (name, appointmentId, newStatus) => {
    const appointmentRef = ref(database, `citas/${name}/${appointmentId}`)
    try {
      await update(appointmentRef, { status: newStatus })
      if (newStatus === 'Confirmado' || newStatus === 'Cancelado') {
        const appointment = appointments.find((a) => a.id === appointmentId)
        if (appointment) {
          let message = ''
          if (newStatus === 'Confirmado') {
            message =
              appointment.type === 'optica'
                ? `👓 *Óptica Natalú* 👓\n\n🌟 *Estimado/a ${appointment.name},*\n\n📅 *Fecha de la cita:* ${format(new Date(appointment.date), 'dd/MM/yyyy', { locale: es })}\n⏰ *Hora:* ${appointment.time}\n\n✅ *Estado de la cita:* Confirmada\n\nNos complace informarte que tu cita en nuestra óptica ha sido confirmada. Te esperamos en nuestras instalaciones, donde estaremos encantados de brindarte el mejor servicio. 😊\n\n📍 *Dirección de la óptica:* Cra. 86f #35B-16 sur, Bogotá\n\nSi tienes alguna consulta o necesitas reprogramar, no dudes en contactarnos. Estaremos felices de ayudarte. 😊`
                : `👓 *Óptica Natalú* 👓\n\n🌟 *Estimado/a ${appointment.name},*\n\n📅 *Fecha de la cita:* ${format(new Date(appointment.date), 'dd/MM/yyyy', { locale: es })}\n⏰ *Hora:* ${appointment.time}\n\n✅ *Estado de la cita:* Confirmada\n\nNos complace informarte que tu cita a domicilio ha sido confirmada. Un especialista visitará tu domicilio en la fecha y hora acordadas. ⌚\n\nSi tienes alguna consulta o necesitas reprogramar, no dudes en contactarnos. Estaremos felices de ayudarte. 😊`
          } else if (newStatus === 'Cancelado') {
            message = `👓 *Óptica Natalú* 👓\n\n🌟 *Estimado/a ${appointment.name},*\n\n📅 *Fecha de la cita:* ${format(new Date(appointment.date), 'dd/MM/yyyy', { locale: es })}\n⏰ *Hora:* ${appointment.time}\n\n❌ *Estado de la cita:* Cancelada\n\nLamentablemente, tu cita ha sido cancelada. Si deseas reagendar, por favor, contáctanos para encontrar una nueva fecha. Lamentamos los inconvenientes y estamos para ayudarte. 😊\n\nSi tienes alguna consulta, no dudes en contactarnos. Estaremos encantados de asistirte.`
          }
          const phoneNumber = `57${appointment.phone.replace(/\D/g, '')}`
          UseWhatsapp(phoneNumber, message)
        }
      }
    } catch (error) {
      console.error('Error al actualizar el estado de la cita:', error)
    }
  }

  const handleSearch = e => { setSearchTerm(e.target.value) }

  const filterItems = items => {
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.phone && item.phone.includes(searchTerm)) ||
        (item.email && item.email.toLowerCase().includes(searchTerm.toLowerCase())),
    )
  }

  const clearAppointments = () => {
    const oneMonthAgo = new Date()
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)

    const appointmentsRef = ref(database, 'citas')

    get(appointmentsRef)
      .then(snapshot => {
        if (snapshot.exists()) {
          const allAppointments = snapshot.val()
          const updatedAppointments = {}

          // Filtramos las citas para cada usuario
          Object.keys(allAppointments).forEach((userId) => {
            updatedAppointments[userId] = {}
            Object.entries(allAppointments[userId]).forEach(([appointmentId, appointment]) => {
              const appointmentDate = new Date(appointment.date)
              if (
                appointmentDate > oneMonthAgo ||
                (appointment.status !== 'Confirmado' && appointment.status !== 'Cancelado')
              ) {
                updatedAppointments[userId][appointmentId] = appointment
              }
            })
            // Si no hay citas para este usuario, eliminamos la entrada
            if (Object.keys(updatedAppointments[userId]).length === 0) {
              delete updatedAppointments[userId]
            }
          })

          // Actualizamos la base de datos con las citas filtradas
          update(appointmentsRef, updatedAppointments)
            .then(() => {
              setAppointments(
                Object.values(updatedAppointments).flatMap((userAppointments) => Object.values(userAppointments)),
              )
              alert('Se han eliminado las citas confirmadas o canceladas de hace más de un mes.')
            })
            .catch((error) => {
              console.error('Error al limpiar las citas:', error)
              alert('Hubo un error al limpiar las citas.')
            })
        }
      })
      .catch((error) => {
        console.error('Error al obtener las citas:', error)
        alert('Hubo un error al obtener las citas.')
      })
  }

  const deleteAppointment = (appointmentId, userId) => {
    const appointmentRef = ref(database, `citas/${userId}/${appointmentId}`)
    if (window.confirm('¿Estás seguro de que deseas eliminar esta cita?')) {
    remove(appointmentRef)
      .then(() => {
        setAppointments(appointments.filter(app => app.id !== appointmentId))
        alert('La cita ha sido eliminada.')
      })
      .catch(error => {
        console.error('Error al eliminar la cita:', error)
        alert('Hubo un error al eliminar la cita.')
      })
    }
  }

  const clearMessages = () => {
    const messagesRef = ref(database, 'contactenos')
    if (window.confirm('¿Estás seguro de que deseas eliminar todos los mensajes?')) {
      remove(messagesRef)
        .then(() => {
          alert('Todos los mensajes han sido eliminados.')
          setMessages([])
        })
        .catch((error) => {
          console.error('Error al borrar los mensajes:', error)
          alert('Hubo un error al borrar los mensajes.')
        })
    }
  }

  return { activeSection, setActiveSection, notifications, isCollapsed, setIsCollapsed, appointments, searchTerm,
    darkMode, reminders, messages, user, loading, logout, dashboardData, toggleDarkMode, handleAppointmentStatus,
    handleSearch, filterItems, clearMessages, clearAppointments, deleteAppointment }
}
